import React, { useEffect, useRef, useCallback } from 'react'
import { PlacementSummary } from '../../../services/api'
import { JWPLAYER_SITE_ID } from '../../../constants/jwplayer'
import pvStyles from '../PagePreview.module.scss'

// JWPlayer only supports one player library per page (all scripts write to the
// same window.jwplayer global), so we load whichever player script is requested
// first and reuse it for all previews. Per-widget player config differences
// (ad rules, skins) won't be reflected here — that's acceptable for CMS preview.
let jwPlayerLoaded: Promise<void> | null = null

const loadJWPlayerScript = (playerId: string): Promise<void> => {
  if ((window as any).jwplayer) return Promise.resolve()
  if (!jwPlayerLoaded) {
    jwPlayerLoaded = new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = `https://cdn.jwplayer.com/libraries/${playerId}.js`
      script.onload = () => resolve()
      script.onerror = () => { jwPlayerLoaded = null; reject(new Error('Failed to load JWPlayer')) }
      document.head.appendChild(script)
    })
  }
  return jwPlayerLoaded
}

const VideoEmbedPreview: React.FC<{ placement: PlacementSummary }> = ({ placement }) => {
  const meta = placement.widget?.metadata || {}
  const videoId = meta.video_id
  const playerId = meta.player_id
  const title = meta.title || placement.name
  const playerRef = useRef<HTMLDivElement>(null)
  const playerInstanceRef = useRef<any>(null)

  const initPlayer = useCallback(async () => {
    if (!videoId || !playerId || !playerRef.current) return
    try {
      await loadJWPlayerScript(playerId)
      const jw = (window as any).jwplayer
      if (!jw) return
      if (playerInstanceRef.current) {
        try { playerInstanceRef.current.remove() } catch { /* ok */ }
      }
      playerInstanceRef.current = jw(playerRef.current).setup({
        playlist: `https://cdn.jwplayer.com/v2/media/${videoId}?site_id=${JWPLAYER_SITE_ID}`,
        width: '100%',
        aspectratio: '16:9',
        autostart: false,
        mute: true,
      })
    } catch { /* player load failed, show fallback */ }
  }, [videoId, playerId])

  useEffect(() => {
    initPlayer()
    return () => {
      if (playerInstanceRef.current) {
        try { playerInstanceRef.current.remove() } catch { /* ok */ }
        playerInstanceRef.current = null
      }
    }
  }, [initPlayer])

  if (!videoId) {
    return (
      <div className={pvStyles.pvVideoPreview}>
        <div className={pvStyles.pvVideoPlaceholder}>
          <span className={pvStyles.pvPlayIcon}>▶</span>
        </div>
        <div className={pvStyles.pvVideoLabel}>{title}</div>
      </div>
    )
  }

  return (
    <div className={pvStyles.pvVideoPreview}>
      <div ref={playerRef} />
      <div className={pvStyles.pvVideoLabel}>{title}</div>
    </div>
  )
}

export default VideoEmbedPreview
